import type * as React from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import Spinner from '@cloudscape-design/components/spinner';
import { useAuth } from './AuthProvider';
import { RestrictedAccess } from '@/routes/RestrictedAccess';

/**
 * Guard único das rotas autenticadas do admin.
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2, Google SSO
 * restrito + guard de superAdmin).
 *
 * Contrato: decide entre 4 destinos pelo `status` do `useAuth()`:
 * - LOADING → spinner mínimo;
 * - SIGNED_OUT → redireciona para `/login` preservando `from` (o Login volta
 *   para a rota pedida);
 * - AUTHORIZED → renderiza `children`;
 * - qualquer outra variante (WRONG_DOMAIN/EMAIL_NOT_VERIFIED/MISSING_CLAIM)
 *   → tela `RestrictedAccess` com mensagem específica + signOut.
 *
 * Usado por: `routes/router.tsx` (envolve o `AdminLayout`, logo tudo menos
 * `/login`).
 *
 * Arquitetura: é só apresentação — quem barra dado é o claim `superAdmin` nas
 * `firestore.rules` e nas callables. Esconder a tela sem a rule não protege
 * nada.
 */
export function RequireSuperAdmin({ children }: { children: ReactNode }): React.JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  if (status.kind === 'LOADING') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner size="large" />
      </div>
    );
  }
  if (status.kind === 'SIGNED_OUT') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (status.kind !== 'AUTHORIZED') {
    return <RestrictedAccess status={status} />;
  }
  return <>{children}</>;
}
